import type {
    CreateExtensionArgument,
    Extension,
    ExtensionAPI,
    ResourceLoaderArgs,
    ResourceLoaderDefinition
} from '@wix/document-manager-core'
import _ from 'lodash'
import type {
    ClientSpecMap,
    BuilderEditorClientSpecMapEntry,
    EditorClientSpecMapEntry
} from '@wix/document-services-types'
import {RESOURCE_TYPES} from '../../constants/constants'
import type {AppsRuntimeDataServer, TransformedAppRuntimeData} from './componentsLoaderUtils'
import {getOverrideId} from '../../utils/resourceLoaderUtils'
import type {PlatformExtensionAPI} from '../platform'
import {mergeComponentsByReplacingBuilderComponents, ReportableError} from '@wix/document-manager-utils'
import type {RMApi} from '../rendererModel'

export interface ComponentsResourceLoaderExtensionAPI extends ExtensionAPI {
    componentsResourceLoader: {
        loadComponents(): Promise<TransformedAppRuntimeData[]>
    }
}

const INTERACTIONS = {
    NO_APPS_FOUND: 'components-loader-no-apps-found',
    FETCH_COMPONENTS: 'components-loader-fetch-components'
}

export const createExtension = ({
    environmentContext,
    experimentInstance,
    logger
}: CreateExtensionArgument): Extension => {
    const createResourceLoader = (args: ResourceLoaderArgs): ResourceLoaderDefinition[] => {
        const {dal, pointers, extensionAPI} = args

        const loadComponents = async (appDefIds: string[]): Promise<TransformedAppRuntimeData[]> => {
            const clientSpecMap = dal.get(pointers.rendererModel.getClientSpecMap()) as ClientSpecMap
            if (!clientSpecMap) {
                throw new Error('Client spec map is not found')
            }

            const appDefinitionIds: {appDefinitionId: string; version?: string}[] = []
            const overrideIds: string[] = []

            appDefIds.forEach(appDefId => {
                const app = Object.values(clientSpecMap).find(
                    appEntry => (appEntry as BuilderEditorClientSpecMapEntry).appDefinitionId === appDefId
                ) as BuilderEditorClientSpecMapEntry

                if (app) {
                    const version = app.appFields?.installedVersion
                    appDefinitionIds.push({
                        appDefinitionId: appDefId,
                        ...(version && {version})
                    })

                    const overrideId = getOverrideId(dal, pointers, appDefId)
                    if (overrideId) {
                        overrideIds.push(overrideId)
                    }
                }
            })

            if (appDefinitionIds.length === 0) {
                logger.interactionStarted(INTERACTIONS.NO_APPS_FOUND)
                logger.interactionEnded(INTERACTIONS.NO_APPS_FOUND, {
                    extras: {
                        requestedAppDefIdsCount: appDefIds.length
                    }
                })
                return []
            }

            const uniqueOverrideIds = _.uniq(overrideIds)
            const serverFacade = environmentContext.serverFacade as AppsRuntimeDataServer
            const languageCode = dal.get(pointers.rendererModel.getLanguageCode())

            logger.interactionStarted(INTERACTIONS.FETCH_COMPONENTS, {
                extras: {
                    appDefinitionIds,
                    uniqueOverrideIds,
                    languageCode
                }
            })

            try {
                const componentsData = await serverFacade.getComponentsByAppIds(
                    appDefinitionIds,
                    uniqueOverrideIds.length > 0 ? uniqueOverrideIds : undefined,
                    languageCode
                )

                const appsWithComponents = componentsData.filter(app => !!app?.components?.length)
                const appsComponentsCounts = _.map(componentsData, app => ({
                    appDefId: app?.appId,
                    count: app?.components?.length ?? 0
                }))

                logger.interactionEnded(INTERACTIONS.FETCH_COMPONENTS, {
                    extras: {
                        appsWithComponentsCount: appsWithComponents.length,
                        appsComponentsCounts
                    }
                })

                return componentsData
            } catch (error: any) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'componentsLoaderFetchComponentsFailed',
                        message: 'Failed to fetch builder components',
                        extras: {
                            errorMessage: error?.message
                        }
                    })
                )

                throw error
            }
        }

        const mergeComponents = (componentsData: TransformedAppRuntimeData[]) => {
            const newCsm = _.cloneDeep(dal.get(pointers.rendererModel.getClientSpecMap()))
            for (const appRuntimeData of componentsData) {
                if (!appRuntimeData.appId || !appRuntimeData.components?.length) {
                    logger.captureError(
                        new ReportableError({
                            errorType: 'componentsLoaderMergeSkipped',
                            message: 'Skipped merging components - missing appId or components',
                            extras: {
                                appId: appRuntimeData.appId,
                                hasComponents: Boolean(appRuntimeData.components?.length)
                            }
                        })
                    )
                    continue
                }

                const clientSpecMapEntryPointer = pointers.rendererModel.getClientSpecMapEntryByAppDefId(
                    appRuntimeData.appId
                )

                const existingEntry = _.cloneDeep(dal.get(clientSpecMapEntryPointer))

                if (!existingEntry) {
                    logger.captureError(
                        new ReportableError({
                            errorType: 'componentsLoaderMergeSkippedNoCsmEntry',
                            message: 'Skipped merging components - no CSM entry found',
                            extras: {
                                appId: appRuntimeData.appId,
                                componentsCount: appRuntimeData.components?.length
                            }
                        })
                    )
                    continue
                }

                existingEntry.components = mergeComponentsByReplacingBuilderComponents(
                    existingEntry.components,
                    appRuntimeData.components
                )

                if (appRuntimeData.appInstallationData?.installationFlow) {
                    existingEntry.appType = appRuntimeData.appInstallationData.installationFlow
                }

                if (appRuntimeData.dependencies) {
                    existingEntry.dependencies = appRuntimeData.dependencies
                }

                if (experimentInstance.isOpen('dm_mergeComponentsToStore')) {
                    if (clientSpecMapEntryPointer?.innerPath?.[0]) {
                        newCsm[clientSpecMapEntryPointer.innerPath[0]] = existingEntry
                    }
                } else {
                    dal.set(clientSpecMapEntryPointer, existingEntry)
                }
            }
            if (experimentInstance.isOpen('dm_mergeComponentsToStore')) {
                const {rendererModel} = extensionAPI as RMApi
                rendererModel.mergeCSMToLastApproved(newCsm)
            }
        }

        const getAvailableResources = () => {
            const clientSpecMap = dal.get(pointers.rendererModel.getClientSpecMap()) as ClientSpecMap
            if (!clientSpecMap) {
                return []
            }

            const clientSpecMapAppIds = _.uniq(
                Object.values(clientSpecMap)
                    .map(appSpec => (appSpec as EditorClientSpecMapEntry).appDefinitionId)
                    .filter(Boolean)
            )

            return _.uniq(clientSpecMapAppIds)
        }

        const getEditorAndHybridResources = () => {
            const clientSpecMap = dal.get(pointers.rendererModel.getClientSpecMap()) as ClientSpecMap

            if (!clientSpecMap) {
                return []
            }

            const clientSpecMapAppIds = _.uniq(
                Object.values(clientSpecMap)
                    .filter(appSpec => {
                        const isEditorOrHybrid = ['Editor', 'Hybrid'].includes(
                            (appSpec as EditorClientSpecMapEntry).appType
                        )
                        const appDefId = (appSpec as EditorClientSpecMapEntry).appDefinitionId
                        const hasOverrideId = appDefId && !!getOverrideId(dal, pointers, appDefId)
                        return isEditorOrHybrid || hasOverrideId
                    })
                    .map(appSpec => (appSpec as EditorClientSpecMapEntry).appDefinitionId)
                    .filter(Boolean)
            )

            return _.uniq(clientSpecMapAppIds)
        }

        const loadAndMergeComponents = async (appDefIds: string[]): Promise<void> => {
            const componentsData = await loadComponents(appDefIds)
            mergeComponents(componentsData)

            const {platform} = extensionAPI as PlatformExtensionAPI
            if (platform?.getClientSpecMapRefreshCallbacks) {
                const csmRefreshCallbacks = platform.getClientSpecMapRefreshCallbacks()
                csmRefreshCallbacks.forEach(csmRefreshCallback => {
                    try {
                        csmRefreshCallback()
                    } catch (error: any) {
                        logger.captureError(
                            new ReportableError({
                                errorType: 'csmRefreshCallbackError',
                                message: error.message,
                                extras: {originalError: JSON.stringify(error)}
                            })
                        )
                    }
                })
            }
        }

        const isBuilderComponentModel = () =>
            pointers.rendererModel.isBuilderComponentModel() &&
            dal.get(pointers.rendererModel.isBuilderComponentModel())

        const load = async (appDefIds: string[]) => {
            if (!isBuilderComponentModel()) {
                return
            }
            await loadAndMergeComponents(appDefIds)
        }

        const loadAll = async () => {
            if (!isBuilderComponentModel()) {
                return
            }
            const filteredAppDefIds = getEditorAndHybridResources()
            await loadAndMergeComponents(filteredAppDefIds)
        }

        const initAvailableResources = async () => {
            if (!isBuilderComponentModel()) {
                return
            }
            return loadAll()
        }

        return [
            {
                resourceType: RESOURCE_TYPES.COMPONENTS,
                load,
                loadAll,
                getAvailableResources,
                initAvailableResources
            }
        ]
    }

    const createExtensionAPI = (): ComponentsResourceLoaderExtensionAPI => {
        return {
            componentsResourceLoader: {
                loadComponents: async () => {
                    throw new Error('loadComponents should be implemented in the resource loader context')
                }
            }
        }
    }

    const initialize = async () => {}

    return {
        name: 'componentsResourceLoader',
        dependencies: new Set(['documentServicesModel']),
        createResourceLoader,
        createExtensionAPI,
        initialize
    }
}
